import { ApiResponse } from '@nohara-dxdev/oidc-core';
import { QueryConfig } from '@nohara-dxdev/react-core';
import { useScopeIds } from '@nohara-dxdev/react-oidc';
import { queryOptions, useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api-client';
import { SettingEntity } from '@/types/api';

import { getSettingsQueryKey } from './get-settings';

export const getSetting = async <T = unknown>(
  orgId: string,
  projectId: string | undefined,
  key: string,
): Promise<SettingEntity<T>> => {
  if (projectId) {
    const { data } = await api.get<unknown, ApiResponse<SettingEntity<T>>>(
      `/orgs/${orgId}/projects/${projectId}/settings/${key}`,
    );
    return data;
  }
  const { data } = await api.get<unknown, ApiResponse<SettingEntity<T>>>(
    `/orgs/${orgId}/settings/${key}`,
  );
  return data;
};

export const getSettingQueryKey = (
  orgId?: string | null,
  projectId?: string | null,
  key?: string | null,
) => {
  const queryKey = getSettingsQueryKey(orgId, projectId);
  if (typeof key === 'string') {
    queryKey.push('key', key);
  }
  return queryKey;
};

export const getSettingQueryOptions = <T = unknown>(
  orgId: string | undefined | null,
  projectId: string | undefined | null,
  key: string | undefined | null,
) => {
  return queryOptions({
    queryKey: getSettingQueryKey(orgId, projectId, key),
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    queryFn: () => getSetting<T>(orgId!, projectId!, key!),
    enabled: !!orgId && typeof projectId === 'string' && typeof key === 'string',
  });
};

interface UseSettingOptions<T = unknown> {
  queryConfig?: QueryConfig<typeof getSettingQueryOptions<T>>;
}

export const useSetting = <T = unknown>(
  key: string,
  { queryConfig = {} }: UseSettingOptions<T> = {},
) => {
  const { orgId } = useScopeIds('org');
  const { projectId } = useScopeIds();

  return useQuery({
    ...getSettingQueryOptions<T>(orgId, projectId, key),
    ...queryConfig,
  });
};
